/**
 * Turbo-based router — Phase 8 replacement of ep-pjax + ajax-router.js.
 *
 * Handles: full-page Turbo Drive navigation, page-transition animations,
 * anchor / back-to-top scrolling, app-state updates (ep:change /
 * ep:beforechange CustomEvents for ESM listeners), and collection-filter
 * frame swaps via <turbo-frame>.
 *
 * Imported directly by ajax-router.esm.js (its callers in IIFE
 * controllers reach through window.ajaxRouter, which is the only
 * surviving global indirection here). No window.appRouter shim is
 * needed — was retired in Phase 11 once ajax-router migrated to ESM
 * and could import { pjaxUrl, scrollToTop } from './router.js' directly.
 *
 * Import paths use flat built names — esbuild's flatResolver plugin maps
 * './app-get.js' → theme/js/core/app-get.esm.js at bundle time.
 *
 * AI: Entirely written by AI
 * Date: 2026-05-02
 * Model: Claude Sonnet 4.6
 */

import * as Turbo from '@hotwired/turbo';
import { log } from './log.js';

const MIN_SCROLL_MS = 150;
const MAX_SCROLL_MS = 1500;

// Set by turbo:visit (navigation click) so turbo:load can skip the initial
// page load — app-config.js fires ep:ready on DOMContentLoaded instead.
let pendingVisit = false;

let rafId = null;

export function init() {
  wireNoAjaxExclusions();
  wireTransitions();
  wireStateUpdates();
  wireAnchorLinks();
  handleInitialHash();
  log('[Router] Turbo router initialized');
}

// Cancel Turbo Drive navigation for .no-ajax links.
// When cancelled, the browser follows the href via normal full-page load —
// correct for /account/logout, etc. Links whose JS handlers already call
// event.preventDefault() on the native click (e.g. remove-line-item) are
// never intercepted by Turbo in the first place (it checks defaultPrevented).
function wireNoAjaxExclusions() {
  document.addEventListener('turbo:click', (event) => {
    if (event.target.closest('.no-ajax')) {
      event.preventDefault();
    }
  });
}

// View Transitions API: root-level fade-in on each navigation. Root VT
// (whole-page snapshot) avoids expensive per-element GPU texture creation.
// Browsers without support skip silently — original render path runs unchanged.
// See _turbo-overrides.scss for animation details.
function wireTransitions() {
  if (!document.startViewTransition) return;
  document.addEventListener('turbo:before-render', wrapRenderInVT);
  document.addEventListener('turbo:before-frame-render', wrapRenderInVT);
}

function wrapRenderInVT(event) {
  const original = event.detail.render;
  event.detail.render = (curr, next) =>
    document.startViewTransition(() => original(curr, next)).updateCallbackDone;
}

function wireStateUpdates() {
  // turbo:visit fires only on actual navigation (not on initial page load).
  document.addEventListener('turbo:visit', (event) => {
    pendingVisit = true;
    if (window.app) window.app.set('loading', true);
    document.dispatchEvent(
      new CustomEvent('ep:beforechange', { detail: { url: event.detail.url } })
    );
  });

  document.addEventListener('turbo:load', (event) => {
    // Skip initial page load — app-config.js fires ep:ready instead.
    if (!pendingVisit) return;
    pendingVisit = false;

    const url = new URL(event.detail.url);
    const gaUrl = url.pathname + url.hash + url.search;

    if (window.app) {
      window.app.set('loading', false);
      window.app.set('url.currentObj', url);

      const content = document.getElementById('content');
      if (content) {
        window.app.set('body_class', content.dataset.bodyClass);
        window.app.set('page_type', content.dataset.pageType);
      }

      window.app.ga('set', 'location', gaUrl, 'Analytics enabled! Sending page update to GA . . .');
      window.app.ga('send', 'pageview');

      if (window.app.get('consent.statistics') && window._gaq) {
        window._gaq.push(['_trackPageview', gaUrl]);
      }
    }

    log('[Router] navigated to ' + gaUrl);

    document.dispatchEvent(new CustomEvent('ep:change', { detail: { url } }));

    if (url.hash) {
      setTimeout(() => scrollToAnchor(url.hash), 250);
    }
  });
}

function wireAnchorLinks() {
  document.addEventListener('click', (event) => {
    const link = event.target.closest("a[href^='#'], button.js-back-to-top");
    if (!link) return;
    event.preventDefault();
    if (link.classList.contains('js-back-to-top')) {
      scrollToTop();
    } else {
      const hash = link.getAttribute('href');
      if (hash && hash.length > 1) scrollToAnchor(hash);
    }
  });
}

// Smooth-scroll to a hash target, offsetting for the sticky header.
export function scrollToAnchor(hash) {
  const target = document.querySelector(hash);
  if (!target) return;
  const header = document.querySelector('#header-global');
  const headerOffset = header?.offsetHeight ?? 0;
  const targetY = target.getBoundingClientRect().top + window.scrollY - headerOffset;
  smoothScrollTo(targetY, durationForDistance(Math.abs(window.scrollY - targetY)));
}

export function scrollToTop() {
  smoothScrollTo(0, durationForDistance(window.scrollY));
}

// Back-compat shim called by collection-filters and common-products-controller
// via the IIFE ajax-router.js stub.
// - Partial swaps (container matches a <turbo-frame>) → set frame.src
// - Full-page navigations → Turbo.visit
export function pjaxUrl(url, container, _maintainScrollPosition) {
  if (container) {
    const frame = document.querySelector(container);
    if (frame && frame.tagName === 'TURBO-FRAME') {
      frame.src = url;
      return;
    }
  }
  Turbo.visit(url);
}

function durationForDistance(distance) {
  const ratio = distance / document.documentElement.scrollHeight;
  return Math.max(MIN_SCROLL_MS, Math.min(MAX_SCROLL_MS, ratio * MAX_SCROLL_MS));
}

function smoothScrollTo(targetY, duration) {
  if (rafId !== null) cancelAnimationFrame(rafId);
  const start = window.scrollY;
  const delta = targetY - start;
  const t0 = performance.now();
  const ease = (x) => (x < 0.5 ? 2 * x * x : 1 - Math.pow(-2 * x + 2, 2) / 2);
  function step(now) {
    const t = Math.min(1, (now - t0) / duration);
    window.scrollTo(0, start + delta * ease(t));
    rafId = t < 1 ? requestAnimationFrame(step) : null;
  }
  rafId = requestAnimationFrame(step);
}

function handleInitialHash() {
  if (window.location.hash) {
    setTimeout(() => scrollToAnchor(window.location.hash), 250);
  }
}
